import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api/client";
import type { AuthUser } from "@/lib/auth/store";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  Home,
  Package,
  FolderTree,
  Users,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Warehouse,
  Barcode,
  ClipboardList,
  ArrowLeftRight,
  Truck,
  Building2,
  ShoppingCart,
  Receipt,
  Monitor,
  Store,
  UserCircle,
  AlertTriangle,
  Wallet,
  Tag,
  ShieldCheck,
  CreditCard,
  Webhook,
  Wrench,
  BarChart2,
  ScrollText,
  Settings2,
  RefreshCcw,
  Upload,
  User,
  PackageSearch,
  Banknote,
} from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { useAuthStore, roleAllows, type UserRole } from "@/lib/auth/store";
import { useLogout } from "@/lib/auth/use-logout";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { KeyboardHelpDialog } from "@/components/ui/keyboard-help-dialog";

const ROUTE_LABELS: Record<string, string> = {
  "/": "Trang chủ",
  "/pos": "Bán lẻ",
  "/orders": "Đơn hàng",
  "/products": "Sản phẩm",
  "/products/new": "Thêm mới",
  "/categories": "Danh mục",
  "/inventory": "Tồn kho",
  "/inventory/serials": "Tra Imei",
  "/inventory/imports": "Nhập hàng",
  "/inventory/imports/new": "Tạo phiếu",
  "/inventory/transfers": "Chuyển kho",
  "/repair": "Sửa chữa",
  "/repair/new": "Tạo mới",
  "/warranty": "Bảo hành",
  "/warranty/new": "Tạo mới",
  "/customers": "Khách hàng",
  "/customers/new": "Thêm mới",
  "/debts": "Công nợ",
  "/debts/overdue": "Quá hạn",
  "/cashfund": "Sổ quỹ",
  "/admin": "Quản trị",
  "/admin/coupons": "Mã giảm giá",
  "/admin/workstations": "Máy POS",
  "/admin/store": "Cửa hàng",
  "/admin/sepay": "SePay",
  "/admin/webhooks": "Webhook Log",
  "/admin/warehouses": "Kho hàng",
  "/admin/suppliers": "NCC",
  "/admin/users": "Người dùng",
  "/admin/import": "Import dữ liệu",
  "/admin/general": "Cài đặt chung",
  "/admin/inventory": "Tồn kho",
  "/admin/inventory/rebuild": "Rebuild tồn kho",
  "/admin/audit-log": "Nhật ký kiểm toán",
  "/reports": "Báo cáo",
  "/reports/revenue": "Doanh thu",
  "/reports/revenue/daily": "Theo ngày",
  "/reports/revenue/by-cashier": "Theo thu ngân",
  "/reports/revenue/by-payment-method": "Thanh toán",
  "/reports/revenue/by-order-type": "Lẻ / Sỉ",
  "/reports/revenue/gross-profit": "Lợi nhuận gộp",
  "/reports/cashbook": "Quỹ tiền",
  "/reports/inventory": "Tồn kho",
  "/reports/inventory/stock-value": "Giá trị tồn",
  "/reports/inventory/low-stock": "Sắp hết hàng",
  "/reports/inventory/slow-moving": "Chậm luân chuyển",
  "/reports/inventory/transactions": "Nhật ký kho",
  "/reports/inventory/import-history": "Lịch sử nhập",
  "/reports/customers": "Khách hàng",
  "/reports/customers/top": "Top khách hàng",
  "/reports/customers/overdue-aging": "Công nợ (aging)",
  "/reports/customers/acquisition": "Khách hàng mới",
  "/reports/staff": "Nhân viên",
  "/reports/staff/cashier": "Doanh thu của tôi",
  "/reports/staff/technician": "Kỹ thuật viên",
  "/account": "Tài khoản",
  "/bao-hanh": "Tra cứu bảo hành",
  "/wholesale": "Bán sỉ",
};

function Breadcrumbs({ pathname }: { pathname: string }) {
  if (pathname === "/login") return null;
  const segments = pathname.split("/").filter(Boolean);
  const crumbs: { path: string; label: string }[] = [
    { path: "/", label: ROUTE_LABELS["/"] },
  ];
  let acc = "";
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    acc += "/" + seg;
    const label = ROUTE_LABELS[acc];
    if (label) {
      crumbs.push({ path: acc, label });
    } else if (seg === "edit") {
      crumbs.push({ path: acc, label: "Chỉnh sửa" });
    } else if (seg === "new") {
      crumbs.push({ path: acc, label: "Tạo mới" });
    } else {
      crumbs.push({ path: acc, label: "Chi tiết" });
    }
  }
  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1 text-sm">
      <ol className="flex min-w-0 items-center gap-1 overflow-hidden">
        {crumbs.map((c, idx) => {
          const isLast = idx === crumbs.length - 1;
          return (
            <li key={c.path} className="flex min-w-0 items-center gap-1">
              {idx > 0 && (
                <ChevronRight size={14} className="shrink-0 text-muted-foreground" />
              )}
              {isLast ? (
                <span className="truncate font-medium text-foreground">{c.label}</span>
              ) : (
                <Link
                  to={c.path}
                  className="truncate text-muted-foreground hover:text-foreground"
                >
                  {c.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
  adminOnly?: boolean;
  roles?: string[];
  // Tailwind text-color class for the icon (readability accent). Applied when
  // the link is not active; the active state uses the primary contrast color.
  color?: string;
}

interface NavGroup {
  label: string;
  icon: ReactNode;
  roles?: string[];
  items: NavItem[];
  color?: string;
}

function isNavGroup(entry: NavItem | NavGroup): entry is NavGroup {
  return "items" in entry;
}

// Trang chủ (home dashboard) is pinned to the very top, rendered on its own
// before the sales group and the rest of NAV.
const HOME_ITEM: NavItem = { label: "Trang chủ", to: "/", icon: <Home size={20} />, color: "text-blue-600 dark:text-blue-400" };

// Role viewer (Chỉ xem): menu rút gọn theo yêu cầu Sếp 28/07 — chỉ Trang chủ,
// Đơn hàng, Sản phẩm, Tồn kho, Dịch vụ (Sửa chữa/Bảo hành), Công nợ, Sổ quỹ,
// Báo cáo. Bổ sung 01/08: Nhập hàng (chị Thủy cần xem phiếu nhập kho — BE vốn
// đã cho viewer GET, chỉ thiếu lối vào menu). Các trang khác viewer vẫn mở được
// qua URL (BE read-only) nhưng không bày trên menu cho gọn.
const VIEWER_MENU = new Set([
  "/orders", "/products", "/inventory", "/inventory/imports",
  "/repair", "/warranty", "/debts", "/cashfund",
]);

// itemVisibleFor — bộ lọc menu DÙNG CHUNG cho mọi chỗ render nav item.
function itemVisibleFor(userRole: string, item: NavItem): boolean {
  if (item.adminOnly) return userRole === "admin";
  if (userRole === "viewer") {
    return item.to === "/" || item.to.startsWith("/reports") || VIEWER_MENU.has(item.to);
  }
  if (item.roles) return roleAllows(userRole as UserRole, item.roles as UserRole[]);
  return true;
}

// "Bán hàng" is a collapsible group holding the sale screens plus the order
// history: Bán lẻ (/pos), Bán sỉ (/wholesale), Đơn hàng (/orders). Kept pinned
// open by default since POS is the most-used area.
const SALES_GROUP: NavGroup = {
  label: "Bán hàng",
  icon: <ShoppingCart size={20} />,
  roles: ["cashier", "manager", "admin"],
  color: "text-emerald-600 dark:text-emerald-400",
  items: [
    { label: "Bán lẻ", to: "/pos", icon: <ShoppingCart size={18} />, roles: ["cashier", "manager", "admin"] },
    { label: "Bán sỉ", to: "/wholesale", icon: <PackageSearch size={18} />, roles: ["cashier", "manager", "admin"] },
    { label: "Đơn hàng", to: "/orders", icon: <Receipt size={18} />, roles: ["cashier", "manager", "admin"] },
  ],
};

// The remaining sidebar entries are grouped by domain. Each group renders as a
// hover-expand section (HoverGroupNav); items inside still filter by role, so a
// group hides entirely when the user can see none of its items.
const NAV: (NavItem | NavGroup)[] = [
  // Sản phẩm & Imei — catalog + IMEI lookup
  {
    label: "Sản phẩm & Imei",
    icon: <Package size={20} />,
    color: "text-violet-600 dark:text-violet-400",
    items: [
      { label: "Sản phẩm", to: "/products", icon: <Package size={18} /> },
      { label: "Danh mục", to: "/categories", icon: <FolderTree size={18} /> },
      { label: "Tra Imei", to: "/inventory/serials", icon: <Barcode size={18} /> },
    ],
  },
  // Kho — tồn kho, nhập, chuyển, quản lý kho
  {
    label: "Kho",
    icon: <Warehouse size={20} />,
    color: "text-amber-600 dark:text-amber-400",
    items: [
      { label: "Tồn kho", to: "/inventory", icon: <Warehouse size={18} /> },
      { label: "Nhập hàng", to: "/inventory/imports", icon: <ClipboardList size={18} />, roles: ["admin", "manager", "warehouse"] },
      { label: "Chuyển kho", to: "/inventory/transfers", icon: <ArrowLeftRight size={18} />, roles: ["admin", "manager", "warehouse"] },
      { label: "Kho hàng", to: "/admin/warehouses", icon: <Building2 size={18} />, adminOnly: true },
    ],
  },
  // Dịch vụ — sửa chữa & bảo hành
  {
    label: "Dịch vụ",
    icon: <Wrench size={20} />,
    color: "text-cyan-600 dark:text-cyan-400",
    items: [
      { label: "Sửa chữa", to: "/repair", icon: <Wrench size={18} />, roles: ["technician", "manager", "admin"] },
      { label: "Bảo hành", to: "/warranty", icon: <ShieldCheck size={18} />, roles: ["cashier", "manager", "admin", "technician"] },
    ],
  },
  // Khách hàng & NCC — khách hàng, công nợ, nhà cung cấp
  {
    label: "Khách hàng & NCC",
    icon: <UserCircle size={20} />,
    color: "text-pink-600 dark:text-pink-400",
    items: [
      { label: "Khách hàng", to: "/customers", icon: <UserCircle size={18} />, roles: ["cashier", "manager", "admin"] },
      { label: "Công nợ", to: "/debts", icon: <Wallet size={18} />, roles: ["manager", "admin", "sales"] },
      { label: "Công nợ quá hạn", to: "/debts/overdue", icon: <AlertTriangle size={18} />, roles: ["manager", "admin", "sales"] },
      { label: "Sổ quỹ", to: "/cashfund", icon: <Banknote size={18} />, roles: ["manager", "admin"] },
      { label: "Nhà cung cấp", to: "/admin/suppliers", icon: <Truck size={18} />, roles: ["admin", "manager", "warehouse"] },
    ],
  },
  // Quản trị — cấu hình & công cụ hệ thống
  {
    label: "Quản trị",
    icon: <Settings2 size={20} />,
    color: "text-slate-500 dark:text-slate-400",
    items: [
      { label: "Mã giảm giá", to: "/admin/coupons", icon: <Tag size={18} />, roles: ["manager", "admin"] },
      { label: "Máy POS", to: "/admin/workstations", icon: <Monitor size={18} />, adminOnly: true },
      { label: "Cửa hàng", to: "/admin/store", icon: <Store size={18} />, adminOnly: true },
      { label: "SePay", to: "/admin/sepay", icon: <CreditCard size={18} />, adminOnly: true },
      { label: "Webhook Log", to: "/admin/webhooks", icon: <Webhook size={18} />, roles: ["admin", "manager"] },
      { label: "Người dùng", to: "/admin/users", icon: <Users size={18} />, adminOnly: true },
      { label: "Import dữ liệu", to: "/admin/import", icon: <Upload size={18} />, adminOnly: true },
      { label: "Cài đặt chung", to: "/admin/general", icon: <Settings2 size={18} />, adminOnly: true },
      { label: "Rebuild tồn kho", to: "/admin/inventory/rebuild", icon: <RefreshCcw size={18} />, adminOnly: true },
      { label: "Nhật ký kiểm toán", to: "/admin/audit-log", icon: <ScrollText size={18} />, adminOnly: true },
    ],
  },
];

// Phase 08 — Reports nav groups
const REPORT_GROUPS: NavGroup[] = [
  {
    label: "Doanh thu",
    icon: <BarChart2 size={16} />,
    roles: ["manager", "admin"],
    items: [
      { label: "Theo ngày", to: "/reports/revenue/daily", icon: <BarChart2 size={15} /> },
      { label: "Theo thu ngân", to: "/reports/revenue/by-cashier", icon: <BarChart2 size={15} /> },
      { label: "Thanh toán", to: "/reports/revenue/by-payment-method", icon: <BarChart2 size={15} /> },
      { label: "Lẻ / Sỉ", to: "/reports/revenue/by-order-type", icon: <BarChart2 size={15} /> },
      { label: "Lợi nhuận gộp", to: "/reports/revenue/gross-profit", icon: <BarChart2 size={15} /> },
      { label: "Quỹ tiền", to: "/reports/cashbook", icon: <BarChart2 size={15} /> },
    ],
  },
  {
    label: "Tồn kho",
    icon: <Warehouse size={16} />,
    roles: ["manager", "admin"],
    items: [
      { label: "Giá trị tồn", to: "/reports/inventory/stock-value", icon: <Warehouse size={15} /> },
      { label: "Sắp hết hàng", to: "/reports/inventory/low-stock", icon: <Warehouse size={15} /> },
      { label: "Chậm luân chuyển", to: "/reports/inventory/slow-moving", icon: <Warehouse size={15} /> },
      { label: "Nhật ký kho", to: "/reports/inventory/transactions", icon: <Warehouse size={15} /> },
      { label: "Lịch sử nhập", to: "/reports/inventory/import-history", icon: <Warehouse size={15} /> },
    ],
  },
  {
    label: "Khách hàng",
    icon: <UserCircle size={16} />,
    roles: ["manager", "admin"],
    items: [
      { label: "Top khách hàng", to: "/reports/customers/top", icon: <UserCircle size={15} /> },
      { label: "Công nợ (aging)", to: "/reports/customers/overdue-aging", icon: <UserCircle size={15} /> },
      { label: "Khách hàng mới", to: "/reports/customers/acquisition", icon: <UserCircle size={15} /> },
    ],
  },
  {
    label: "Nhân viên",
    icon: <Users size={16} />,
    roles: ["manager", "admin", "cashier"],
    items: [
      { label: "Doanh thu của tôi", to: "/reports/staff/cashier", icon: <Users size={15} />, roles: ["cashier", "manager", "admin"] },
      { label: "Kỹ thuật viên", to: "/reports/staff/technician", icon: <Users size={15} />, roles: ["manager", "admin"] },
    ],
  },
];

const ROLE_LABEL: Record<string, string> = {
  admin: "Quản trị",
  manager: "Quản lý",
  cashier: "Thu ngân",
  warehouse: "Kho",
  sales: "Bán hàng",
  technician: "Kỹ thuật",
};

function NavLink({
  item,
  pathname,
  onClick,
  color,
}: {
  item: NavItem;
  pathname: string;
  onClick: () => void;
  color?: string;
}) {
  const active = pathname === item.to || (item.to !== "/" && pathname.startsWith(item.to));
  const iconColor = color ?? item.color;
  return (
    <Link
      to={item.to}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] transition-colors",
        active
          ? "bg-primary font-semibold text-primary-foreground shadow-sm"
          : "font-medium text-foreground/90 hover:bg-accent hover:text-accent-foreground",
      )}
    >
      <span className={cn("shrink-0", !active && iconColor)}>{item.icon}</span>
      <span className="truncate">{item.label}</span>
      {active && <ChevronRight size={16} className="ml-auto shrink-0" />}
    </Link>
  );
}

function ReportGroupNav({
  group,
  userRole,
  pathname,
  onNavigate,
  defaultOpen,
}: {
  group: NavGroup;
  userRole: string;
  pathname: string;
  onNavigate: () => void;
  defaultOpen?: boolean;
}) {
  const visibleItems = group.items.filter((item) => itemVisibleFor(userRole, item));
  // Hooks must run unconditionally (before any early return), so compute
  // initial open-state here and bail out only afterwards.
  const isAnyActive = visibleItems.some((item) => pathname.startsWith(item.to));
  const [open, setOpen] = useState(isAnyActive || !!defaultOpen);

  if (visibleItems.length === 0) return null;

  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] font-semibold transition-colors",
          isAnyActive
            ? "text-primary"
            : "text-foreground hover:bg-accent hover:text-accent-foreground",
        )}
      >
        <span className={cn("shrink-0", !isAnyActive && group.color)}>{group.icon}</span>
        <span className="flex-1 text-left">{group.label}</span>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {open && (
        <div className="ml-3 mt-0.5 space-y-0.5 border-l-2 pl-3">
          {visibleItems.map((item) => (
            <NavLink key={item.to} item={item} pathname={pathname} onClick={onNavigate} color={group.color} />
          ))}
        </div>
      )}
    </div>
  );
}

// Sidebar group that reveals its sub-items on hover (and toggles on click, so
// it also works on touch). Stays open while one of its routes is active.
function HoverGroupNav({
  group,
  userRole,
  pathname,
  onNavigate,
}: {
  group: NavGroup;
  userRole: string;
  pathname: string;
  onNavigate: () => void;
}) {
  const visibleItems = group.items.filter((item) => itemVisibleFor(userRole, item));
  const isAnyActive = visibleItems.some((item) => pathname.startsWith(item.to));
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);

  if (visibleItems.length === 0) return null;

  const open = hover || pinned || isAnyActive;

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <button
        onClick={() => setPinned((p) => !p)}
        className={cn(
          "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] font-semibold transition-colors",
          isAnyActive
            ? "text-primary"
            : "text-foreground hover:bg-accent hover:text-accent-foreground",
        )}
      >
        <span className={cn("shrink-0", !isAnyActive && group.color)}>{group.icon}</span>
        <span className="flex-1 text-left">{group.label}</span>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {open && (
        <div className="ml-3 mt-0.5 space-y-0.5 border-l-2 pl-3">
          {visibleItems.map((item) => (
            <NavLink key={item.to} item={item} pathname={pathname} onClick={onNavigate} color={group.color} />
          ))}
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const [open, setOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const navigate = useNavigate();

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-accent transition-colors cursor-pointer"
      >
        <span className="text-sm text-muted-foreground hidden md:inline">{user?.email}</span>
        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
          {ROLE_LABEL[user?.role ?? ""] ?? user?.role}
        </span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-md border bg-card shadow-lg py-1">
            <button
              onClick={() => { setOpen(false); void navigate({ to: "/account" }); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors"
            >
              <User size={16} />
              Tài khoản
            </button>
            <div className="my-1 border-t" />
            <button
              onClick={() => { setOpen(false); logout.mutate(); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-accent transition-colors"
              disabled={logout.isPending}
            >
              <LogOut size={16} />
              Đăng xuất
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const accessToken = useAuthStore((s) => s.accessToken);

  // Đồng bộ quyền từ server: `user` trong store là bản chụp lúc ĐĂNG NHẬP
  // (persist localStorage) — Sếp đổi quyền trong Quản trị → Nhân viên thì máy
  // nhân viên không hề biết cho tới khi đăng nhập lại. Làm mới từ /auth/me khi
  // mở app + mỗi lần quay lại tab, để cấp/thu quyền có hiệu lực chỉ với F5.
  const { data: freshUser } = useQuery({
    queryKey: ["auth-me"],
    queryFn: () => apiGet<AuthUser>("/v1/auth/me"),
    enabled: !!accessToken,
    refetchOnWindowFocus: true,
    staleTime: 60_000,
  });
  useEffect(() => {
    if (freshUser && JSON.stringify(freshUser) !== JSON.stringify(user)) {
      setUser(freshUser);
    }
  }, [freshUser, user, setUser]);
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;

  const userRole = user?.role ?? "";

  const visibleNav = NAV.filter((entry) => {
    if (isNavGroup(entry)) {
      // Nhóm hiện khi role qua gate nhóm; item bên trong còn lọc tiếp
      // (itemVisibleFor) và nhóm rỗng tự ẩn trong HoverGroupNav.
      return !entry.roles || !user || roleAllows(userRole as UserRole, entry.roles as UserRole[]);
    }
    return itemVisibleFor(userRole, entry);
  });

  // Determine which report groups to show (viewer/sales đi qua roleAllows;
  // từng nhóm bên trong vẫn tự lọc theo group.roles)
  const showReports = roleAllows(userRole as UserRole, ["manager", "admin", "cashier"]);
  const isReportPath = pathname.startsWith("/reports");

  return (
    <div className="flex min-h-screen bg-background">
      {open && (
        <div
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 flex w-64 flex-col border-r bg-card transition-transform duration-200 lg:static lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex h-16 items-center border-b px-4">
          <span className="text-lg font-bold tracking-tight text-primary">Quản lý bán hàng</span>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {/* Trang chủ pinned to top */}
          <NavLink item={HOME_ITEM} pathname={pathname} onClick={() => setOpen(false)} />

          {/* Bán hàng group — Bán lẻ / Bán sỉ (separate screens) */}
          {(!SALES_GROUP.roles || roleAllows(userRole as UserRole, SALES_GROUP.roles as UserRole[])) && (
            <ReportGroupNav
              group={SALES_GROUP}
              userRole={userRole}
              pathname={pathname}
              onNavigate={() => setOpen(false)}
              defaultOpen
            />
          )}

          {visibleNav.map((entry) =>
            isNavGroup(entry) ? (
              <HoverGroupNav
                key={entry.label}
                group={entry}
                userRole={userRole}
                pathname={pathname}
                onNavigate={() => setOpen(false)}
              />
            ) : (
              <NavLink key={entry.to} item={entry} pathname={pathname} onClick={() => setOpen(false)} />
            ),
          )}

          {/* Phase 08 — Reports section */}
          {showReports && (
            <div className="pt-2">
              <button
                onClick={() => setReportsOpen((o) => !o)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] font-semibold transition-colors",
                  isReportPath
                    ? "bg-primary/10 text-primary"
                    : "text-foreground hover:bg-accent hover:text-accent-foreground",
                )}
              >
                <span className={cn("shrink-0", !isReportPath && "text-indigo-600 dark:text-indigo-400")}>
                  <BarChart2 size={20} />
                </span>
                <span className="flex-1 text-left">Báo cáo</span>
                {reportsOpen || isReportPath ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>

              {(reportsOpen || isReportPath) && (
                <div className="ml-2 mt-1 space-y-0.5">
                  {REPORT_GROUPS.map((group) => {
                    if (group.roles && !roleAllows(userRole as UserRole, group.roles as UserRole[])) return null;
                    return (
                      <ReportGroupNav
                        key={group.label}
                        group={group}
                        userRole={userRole}
                        pathname={pathname}
                        onNavigate={() => setOpen(false)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </nav>

      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 items-center gap-4 border-b bg-card px-4">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </Button>

          <Breadcrumbs pathname={pathname} />

          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            {user && <UserMenu />}
          </div>
        </header>

        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>

      {/* Global keyboard help — ? opens dialog */}
      <KeyboardHelpDialog />
    </div>
  );
}
